import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useConfirmation } from '../ui/ConfirmationDialog';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle, 
  Users, 
  Target, 
  TrendingUp,
  FileText,
  Clock,
  RefreshCw,
  Phone,
  ThumbsUp,
  Lightbulb,
  Download,
  AlertTriangle,
  Zap
} from 'lucide-react';

import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ResponsiveContainer } from '../ui/ResponsiveContainer';
import { EmailButton } from '../calls/EmailButton';
import { apiClient } from '../../lib/api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useJobs } from '../../contexts/JobContext';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { StatusUpdateModal } from './StatusUpdateModal';
import { Edit } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface AnalysisStatus {
  status: 'processing' | 'completed' | 'failed' | 'not_found';
  progress: number;
  message: string;
  analysisId?: string;
}

interface AnalysisResults {
  candidate_id: string;
  requirement_id: string;
  analysis_results: {
    analysis: {
      overall_fitment_score: number;
      skill_match_score: number;
      experience_match_score: number;
      location_match_score: number;
      education_match_score: number;
      cultural_fit_score: number;
    };
    skill_match_analysis: {
      mandatory_skills_match: string[];
      optional_skills_match: string[];
      missing_mandatory_skills: string[];
      missing_optional_skills: string[];
      skill_match_percentage: number;
      critical_skill_gaps: string[];
      nice_to_have_skills: string[];
    };
    experience_analysis: {
      candidate_experience_years: number;
      required_experience_years: number;
      experience_gap_years: number;
      experience_assessment: string;
      seniority_level_match: boolean;
      domain_experience_match: boolean;
      leadership_experience_match: boolean;
    };
    location_analysis: {
      location_match: boolean;
      relocation_required: boolean;
      remote_work_compatibility: boolean;
      location_assessment: string;
    };
    education_analysis: {
      education_level_match: boolean;
      degree_relevance: string;
      certification_match: boolean;
      required_education?: string;
      candidate_education?: string;
      level_comparison?: string;
      field_of_study_match?: 'high' | 'medium' | 'low' | string;
      institution_tier_match?: boolean;
      similarities?: string[];
      differences?: string[];
      score_reason?: string;
      education_gaps: string[];
    };
    compensation_analysis: {
      salary_expectation_available: boolean;
      salary_range_compatibility: string;
      compensation_concerns: string[];
    };
    strengths: string[];
    concerns: string[];
    hygiene_questions: Array<{
      question: string;
      category: string;
      priority: string;
      reason: string;
    }>;
    recruiter_insights: {
      interview_focus_areas: string[];
      technical_screening_questions: string[];
      behavioral_questions: string[];
      red_flags_to_watch: string[];
      selling_points: string[];
      negotiation_leverage: string[];
    };
    recommendation: {
      overall_recommendation: string;
      confidence_level: string;
      immediate_actions: string[];
      follow_up_required: boolean;
      additional_screening_needed: string[];
      timeline_recommendation: string;
    };
    analysis_timestamp?: string;
    analysis_method?: string;
  };
  status: string;
  message: string;
  created_at?: string;
  updated_at?: string;
}

const ADMIN_SCOPE = 'all:admin';

const CandidateAnalysisPage: React.FC = () => {
  const { candidateId, requirementId } = useParams<{ candidateId: string; requirementId: string }>();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const isAdmin = Boolean(hasRole('admin') || user?.scopes?.includes(ADMIN_SCOPE));
  const { addLocal } = useJobs();
  const { confirm } = useConfirmation();
  
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>({
    status: 'processing',
    progress: 0,
    message: 'Loading analysis...'
  });
  
  // Status update state
  const [statusUpdateModalOpen, setStatusUpdateModalOpen] = useState(false);
  const [currentSubmissionStatus, setCurrentSubmissionStatus] = useState<string>('Uploaded');
  const [isReprocessing, setIsReprocessing] = useState(false);
  
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [hasFoundResults, setHasFoundResults] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const maxRetries = 60; // 5 minutes with 5-second intervals

  // Initial check for existing analysis results
  useEffect(() => {
    if (!candidateId || !requirementId || !isInitialLoad) return;

    const checkExistingAnalysis = async () => {
      try {
        console.log('🔍 Checking for existing analysis...');
        const results = await apiClient.getCandidateAnalysisResults(candidateId, requirementId);
        console.log('📊 Analysis results:', results);
        
        if (results && results.analysis_results && results.analysis_results.analysis) {
          // Analysis already completed
          console.log('✅ Found existing analysis, showing results immediately');
          setAnalysisResults(results);
          setAnalysisStatus({
            status: 'completed',
            progress: 100,
            message: 'Analysis completed successfully!'
          });
          setIsInitialLoad(false);
          setIsPolling(false); // Ensure polling is disabled
          setHasFoundResults(true); // Mark that we found results
          return;
        }

        // No analysis results yet, start polling
        console.log('❌ No existing analysis found, starting polling');
        setAnalysisStatus({
          status: 'processing',
          progress: 0,
          message: 'Starting JD vs CV analysis...'
        });
        try {
          if (!jobId && candidateId && requirementId) {
            const start = await apiClient.jobsStart({ user_id: user?.id || 'me', type: 'jd_cv_analysis', anchors: { candidate_id: candidateId, requirement_id: requirementId }, idempotency_key: `${candidateId}:${requirementId}:jd_cv` });
            setJobId(start.job.id);
            addLocal(start.job.id);
            await apiClient.jobsUpdate(start.job.id, { status: 'processing', anchors: { candidate_id: candidateId, requirement_id: requirementId }, resume_url: `/candidates/${candidateId}/analysis/${requirementId}` });
          }
        } catch (e) { console.warn('Job start failed', e); }
        setIsPolling(true);
        setIsInitialLoad(false);
      } catch (error) {
        console.error('Error checking existing analysis:', error);
        // Start polling anyway
        setAnalysisStatus({
          status: 'processing',
          progress: 0,
          message: 'Starting JD vs CV analysis...'
        });
        setIsPolling(true);
        setIsInitialLoad(false);
      }
    };

    checkExistingAnalysis();
  }, [candidateId, requirementId, isInitialLoad]);

  // Poll for analysis status
  useEffect(() => {
    if (!candidateId || !requirementId || !isPolling || hasFoundResults) return;

    console.log('🔄 Polling started for analysis status...');

    const pollAnalysisStatus = async () => {
      try {
        // Check if analysis is completed by looking for analysis results
        const results = await apiClient.getCandidateAnalysisResults(candidateId, requirementId);
        console.log('📊 Polling results:', results);
        
        if (results) {
          console.log('✅ Polling found results, stopping polling');
          setAnalysisResults(results);
          setAnalysisStatus({
            status: 'completed',
            progress: 100,
            message: 'Analysis completed successfully!'
          });
          try { if (jobId) await apiClient.jobsUpdate(jobId, { status: 'completed', result_url: `/candidates/${candidateId}/analysis/${requirementId}` }); } catch {}
          setIsPolling(false);
          setHasFoundResults(true);
          return;
        }

        // If no results yet, trigger the analysis if it hasn't been started
        if (retryCount === 0) {
          try {
            console.log('🚀 Triggering JD vs CV analysis...');
            await apiClient.triggerJDCVAnalysis(candidateId, requirementId);
            setAnalysisStatus(prev => ({
              ...prev,
              message: 'JD vs CV analysis started...'
            }));
            try {
              if (!jobId) {
                const start = await apiClient.jobsStart({ user_id: user?.id || 'me', type: 'jd_cv_analysis', anchors: { candidate_id: candidateId, requirement_id: requirementId }, idempotency_key: `${candidateId}:${requirementId}:jd_cv` });
                setJobId(start.job.id);
                addLocal(start.job.id);
                await apiClient.jobsUpdate(start.job.id, { status: 'processing', anchors: { candidate_id: candidateId, requirement_id: requirementId }, resume_url: `/candidates/${candidateId}/analysis/${requirementId}` });
              }
            } catch (e) { console.warn('Job ensure failed', e); }
          } catch (error: any) {
            console.error('Failed to trigger analysis:', error);
            // Continue polling in case analysis was already triggered
          }
        }

        // If no results yet, check candidate status
        const candidateStatus = await apiClient.getResumeProcessingStatus(candidateId);
        
        if (candidateStatus.status === 'completed') {
          setAnalysisStatus(prev => ({
            ...prev,
            progress: Math.min(prev.progress + 10, 90),
            message: 'CV processing complete, running JD vs CV analysis...'
          }));
        } else if (candidateStatus.status === 'comprehensive_ai_processing') {
          setAnalysisStatus(prev => ({
            ...prev,
            progress: Math.min(prev.progress + 5, 85),
            message: 'Running comprehensive AI analysis...'
          }));
        } else if (candidateStatus.status === 'failed') {
          setAnalysisStatus({
            status: 'failed',
            progress: 0,
            message: 'Analysis failed. Please try again.'
          });
          try { if (jobId) await apiClient.jobsUpdate(jobId, { status: 'failed', message: 'Analysis failed' }); } catch {}
          setIsPolling(false);
          return;
        }

        // Continue polling if not completed and within retry limit
        if (retryCount < maxRetries) {
          setRetryCount(prev => prev + 1);
          setTimeout(pollAnalysisStatus, 10000); // Poll every 10 seconds
        } else {
          setAnalysisStatus({
            status: 'failed',
            progress: 0,
            message: 'Analysis timeout. Please try again.'
          });
          setIsPolling(false);
        }

      } catch (error: any) {
        console.error('Error polling analysis status:', error);
        
        if (retryCount < maxRetries) {
          setRetryCount(prev => prev + 1);
          setTimeout(pollAnalysisStatus, 10000);
        } else {
          setAnalysisStatus({
            status: 'failed',
            progress: 0,
            message: 'Failed to check analysis status'
          });
          setIsPolling(false);
        }
      }
    };

    // Start polling after 30 seconds delay
    const timeoutId = setTimeout(pollAnalysisStatus, 30000);
    return () => clearTimeout(timeoutId);
  }, [candidateId, requirementId, isPolling, retryCount, hasFoundResults]);

  const handleRetry = () => {
    setRetryCount(0);
    setIsPolling(true);
    setAnalysisStatus({
      status: 'processing',
      progress: 0,
      message: 'Retrying analysis...'
    });
    setAnalysisResults(null);
  };

  const handleAdminReprocess = () => {
    if (!candidateId || !requirementId) {
      toast.error('Candidate or requirement context missing for reprocess.');
      return;
    }

    confirm({
      title: 'Re-run JD vs CV analysis?',
      description: 'This will rerun the AI processing with the existing resume and refresh candidate details.',
      variant: 'warning',
      confirmText: 'Reprocess',
      onConfirm: async () => {
        try {
          setIsReprocessing(true);
          const response = await apiClient.adminReprocessCandidateJDCV(candidateId, requirementId, {
            clearIdentity: true,
          });
          toast.success(response?.message || 'JD vs CV analysis reprocessing queued');
          setAnalysisResults(null);
          setAnalysisStatus({
            status: 'processing',
            progress: 0,
            message: response?.message || 'JD vs CV analysis reprocessing queued...'
          });
          setRetryCount(1);
          setHasFoundResults(false);
          setIsPolling(true);
          setIsInitialLoad(false);
          setJobId(null);
        } catch (error: any) {
          const detail = error?.response?.data?.detail || error?.message || 'Failed to queue reprocessing';
          toast.error(detail);
          throw error;
        } finally {
          setIsReprocessing(false);
        }
      },
    });
  };

  const renderReprocessButton = (extraClasses = '') => {
    if (!isAdmin) {
      return null;
    }

    const disabled = isReprocessing || !candidateId || !requirementId;
    const baseClasses = 'glass-button hover:glass-button-hover transition-all duration-300 flex items-center gap-2';

    return (
      <Button
        type="button"
        variant="outline"
        onClick={handleAdminReprocess}
        disabled={disabled}
        className={`${baseClasses} ${extraClasses}`.trim()}
      >
        {isReprocessing ? (
          <>
            <LoadingSpinner size="sm" variant="ai" />
            Reprocessing...
          </>
        ) : (
          <>
            <RefreshCw className="w-4 h-4" />
            Reprocess JD/CV
          </>
        )}
      </Button>
    );
  };

  const getStatusIcon = () => {
    switch (analysisStatus.status) {
      case 'processing':
        return <LoadingSpinner size="md" variant="ai" className="text-blue-500" />;
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Clock className="w-6 h-6 text-gray-500" />;
    }
  };


  // Show loading spinner during initial load
  if (isInitialLoad) {
    return (
      <ResponsiveContainer>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <LoadingSpinner size="lg" variant="ai" className="text-blue-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Loading analysis...</p>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (analysisStatus.status === 'processing') {
    return (
      <ResponsiveContainer>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="flex items-center justify-between gap-4 mb-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate(-1)}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                {isAdmin ? renderReprocessButton('ml-auto') : null}
              </div>
              <div className="flex items-center gap-3 mb-2">
                {getStatusIcon()}
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  JD vs CV Analysis
                </h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Analyzing candidate fitment against job requirements
              </p>
            </div>

            {/* Progress Card */}
            <Card className="p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  {getStatusIcon()}
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {analysisStatus.message}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  This may take up to 5 minutes. Please don't close this page.
                </p>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-4">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${analysisStatus.progress}%` }}
                />
              </div>
              
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {analysisStatus.progress}% complete
              </p>
            </Card>

            {/* Analysis Steps */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  CV Processing
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Extract candidate information from CV
                </p>
              </Card>

              <Card className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  {analysisStatus.progress > 30 ? (
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  ) : (
                    <LoadingSpinner size="md" variant="ai" className="text-blue-600" />
                  )}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  AI Analysis
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Comprehensive skill and experience matching
                </p>
              </Card>

              <Card className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                  {analysisStatus.progress > 80 ? (
                    <CheckCircle className="w-6 h-6 text-purple-600" />
                  ) : (
                    <Clock className="w-6 h-6 text-purple-600" />
                  )}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Report Generation
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Generate comprehensive analysis report
                </p>
              </Card>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (analysisStatus.status === 'failed') {
    return (
      <ResponsiveContainer>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              
              <div className="flex items-center gap-3 mb-2">
                {getStatusIcon()}
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Analysis Failed
                </h1>
              </div>
            </div>

            <Card className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Analysis Failed
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {analysisStatus.message}
              </p>
              <Button onClick={handleRetry}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry Analysis
              </Button>
            </Card>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (!analysisResults || !analysisResults.analysis_results || !analysisResults.analysis_results.analysis) {
    return null;
  }

  const analysis = analysisResults.analysis_results.analysis;
  const skillAnalysis = analysisResults.analysis_results.skill_match_analysis;
  const jdMandatory = (skillAnalysis as any)?.jd_mandatory_skills || [];
  const jdOptional = (skillAnalysis as any)?.jd_optional_skills || [];
  const matchedMandatory = skillAnalysis.mandatory_skills_match || [];
  const matchedOptional = skillAnalysis.optional_skills_match || [];
  const experienceAnalysis = analysisResults.analysis_results.experience_analysis;
  const educationAnalysis = analysisResults.analysis_results.education_analysis;
  const hygieneQuestions = analysisResults.analysis_results.hygiene_questions || [];
  const recruiterInsights = analysisResults.analysis_results.recruiter_insights;
  
  // Debug logging
  console.log('🔍 Debug - analysisResults:', analysisResults);
  console.log('🔍 Debug - hygieneQuestions:', hygieneQuestions);
  console.log('🔍 Debug - hygieneQuestions.length:', hygieneQuestions.length);
  console.log('🔍 Debug - recruiterInsights:', recruiterInsights);
  const strengths = analysisResults.analysis_results.strengths || [];
  const concerns = analysisResults.analysis_results.concerns || [];
  
  const overallScore = analysis.overall_fitment_score;

  // 5% step color scale (red → amber/yellow → green/teal)
  const getScoreColor = (score: number | string) => {
    const raw = typeof score === 'string' ? parseFloat(score) : score;
    const s = Math.max(0, Math.min(100, Math.round(isNaN(raw as number) ? 0 : (raw as number))));
    const bucket = Math.floor(s / 5);
    const palette = [
      'text-red-700','text-red-700','text-red-600','text-red-500',
      'text-orange-600','text-orange-500','text-amber-600','text-amber-500',
      'text-yellow-600','text-yellow-500','text-lime-600','text-lime-500',
      'text-green-600','text-green-500','text-green-500','text-emerald-600',
      'text-emerald-500','text-teal-600','text-teal-500','text-teal-500','text-teal-500'
    ];
    return palette[Math.min(palette.length - 1, bucket)];
  };


  // Helper function to get match status
  const getMatchStatus = (score: number | string) => {
    const numScore = typeof score === 'string' ? parseFloat(score) : score;
    if (numScore >= 80) return { status: 'Excellent Match', color: 'text-green-600', bg: 'bg-green-100/50' };
    if (numScore >= 60) return { status: 'Good Match', color: 'text-amber-600', bg: 'bg-amber-100/50' };
    return { status: 'Needs Improvement', color: 'text-red-600', bg: 'bg-red-100/50' };
  };

  // Get candidate contact info from form_data
  const formData = (analysisResults.analysis_results as any)?.form_data || {};
  const candidatePhone = formData.phone || 'Not available';

  const handleDownloadResume = async () => {
    try {
      const candidateIdParam = candidateId || '';
      const { url } = await (await import('../../lib/api/client')).apiClient.getCandidateResumeUrl(candidateIdParam, { disposition: 'attachment', requirementId: requirementId || undefined });
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error('Failed to get resume URL', e);
      confirm({
        title: 'Download Failed',
        description: 'Failed to download resume. Please try again.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
    }
  };

  const handleOpenResume = async () => {
    try {
      const candidateIdParam = candidateId || '';
      const { url } = await (await import('../../lib/api/client')).apiClient.getCandidateResumeUrl(candidateIdParam, { disposition: 'inline', requirementId: requirementId || undefined });
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      console.error('Failed to open resume', e);
      confirm({
        title: 'Open Failed',
        description: 'Failed to open resume. Please try again.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
    }
  };

  return (
    <ResponsiveContainer>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 relative overflow-hidden py-6">
        {/* Glassmorphic background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with Back Button */}
          <div className="flex items-center justify-between mb-6 gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Candidate Analysis</h1>
            {isAdmin ? renderReprocessButton() : <div className="w-20" />}
          </div>


          {/* Candidate & JD Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Candidate Details */}
            <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-xl p-6 lg:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-xl">
                    <Users className="w-6 h-6 text-blue-500" />
                  </div>
                  Candidate Profile
                </h3>
                <div className="flex gap-2">
                  <EmailButton
                    candidateId={candidateId || ''}
                    requirementId={requirementId || ''}
                    candidateName={formData.name || 'Candidate'}
                    candidateEmail={formData.email || ''}
                  />
                  <button onClick={handleDownloadResume} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-glass-border/40 hover:bg-glass-surface/40 text-sm">
                    <Download className="w-4 h-4" />
                    Download Resume
                  </button>
                  <button onClick={handleOpenResume} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-primary-600 dark:text-primary-300 hover:text-primary-300 hover:bg-primary-500/20">
                    Open Resume
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-blue-100 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Name:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formData.name || 'Not provided'}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-100 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Email:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formData.email || 'Not provided'}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-100 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Experience:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formData.experience_years || 0} years</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-blue-100 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Current Role:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formData.current_position || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Location:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formData.location || 'Not specified'}</span>
                </div>
                <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-sm text-blue-800 dark:text-blue-200 font-medium">Contact: {candidatePhone}</div>
                </div>
                {/* Status Section */}
                {requirementId && (
                  <div className="mt-4 pt-4 border-t border-blue-100 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Status:</span>
                      <button
                        onClick={() => setStatusUpdateModalOpen(true)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/30 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        {currentSubmissionStatus}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* JD Details */}
            <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-xl p-6 lg:p-8">
              <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary mb-6 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl">
                  <FileText className="w-6 h-6 text-green-500" />
                </div>
                Job Requirements
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-green-100 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Position:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">Senior Developer</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-green-100 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Department:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">Engineering</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-green-100 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Experience Required:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">5+ years</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-green-100 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Key Skills:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">Python, React, Leadership</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Location:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">Remote/Hybrid</span>
                </div>
              </div>
            </div>
          </div>

          {/* Scoring System Explanation */}
          <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-xl p-6 mb-6">
            <h3 className="text-lg font-bold text-text-enterprise-primary mb-4 flex items-center gap-2">
              <div className="p-1.5 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg">
                <div className="w-5 h-5 text-blue-500">📊</div>
              </div>
              Scoring System
            </h3>
            <div className="bg-info-500/10 border border-info-500/30 rounded-lg p-4">
              <div className="text-info-700 dark:text-info-300 text-sm leading-relaxed">
                <div className="font-semibold mb-2">How Our Scoring Works:</div>
                <ul className="space-y-1 text-xs">
                  <li>• <strong>Skill Match Score:</strong> Reflects technical skills alignment with mandatory requirements</li>
                  <li>• <strong>Overall Fitment Score:</strong> Comprehensive assessment including skills (60%), experience (25%), and education (15%)</li>
                  <li>• <strong>Critical Impact:</strong> Missing ANY mandatory skill significantly reduces both scores to ensure accuracy</li>
                  <li>• <strong>Consistency:</strong> Both scores use the same penalty logic for missing critical requirements</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Key Decision Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Overall Fit Score */}
            <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-xl p-6 lg:p-8 text-center">
              <div className={`text-5xl lg:text-6xl font-bold mb-3 ${getScoreColor(overallScore)}`}>
                {overallScore}%
              </div>
              <div className="text-base lg:text-lg font-bold text-text-enterprise-primary mb-3">Overall Fit</div>
              <div className={`text-sm px-4 py-2 rounded-full font-semibold ${getMatchStatus(overallScore).bg} ${getMatchStatus(overallScore).color}`}>
                {getMatchStatus(overallScore).status}
              </div>
            </div>

            {/* Skills Match */}
            <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-xl p-6 lg:p-8 text-center">
              <div className={`text-5xl lg:text-6xl font-bold mb-3 ${getScoreColor(analysis.skill_match_score)}`}>
                {analysis.skill_match_score}%
              </div>
              <div className="text-base lg:text-lg font-bold text-text-enterprise-primary mb-3">Skills Match</div>
              <div className="text-sm text-text-enterprise-tertiary font-semibold mb-2">
                {skillAnalysis?.mandatory_skills_match?.length || 0} mandatory matched
              </div>
              {/* Explanatory text for score consistency */}
              <div className="text-xs text-text-enterprise-tertiary/80 leading-relaxed">
                {skillAnalysis?.missing_mandatory_skills?.length > 0 ? (
                  <div className="bg-warning-500/10 border border-warning-500/30 rounded-lg p-2 mt-2">
                    <div className="text-warning-600 dark:text-warning-400 font-semibold mb-1">
                      ⚠️ Missing Critical Skills
                    </div>
                    <div className="text-warning-700 dark:text-warning-300">
                      Missing {skillAnalysis.missing_mandatory_skills.length} mandatory skill{skillAnalysis.missing_mandatory_skills.length > 1 ? 's' : ''}: {skillAnalysis.missing_mandatory_skills.slice(0, 2).join(', ')}{skillAnalysis.missing_mandatory_skills.length > 2 ? '...' : ''}
                    </div>
                    <div className="text-warning-600 dark:text-warning-400 text-xs mt-1">
                      This significantly impacts both skill match and overall fitment scores
                    </div>
                  </div>
                ) : (
                  <div className="text-success-600 dark:text-success-400 text-xs">
                    ✓ All mandatory skills present - scores reflect true technical match
                  </div>
                )}
              </div>
            </div>

            {/* Experience Match */}
            <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-xl p-6 lg:p-8 text-center">
              <div className={`text-5xl lg:text-6xl font-bold mb-3 ${getScoreColor(analysis.experience_match_score)}`}>
                {analysis.experience_match_score}%
              </div>
              <div className="text-base lg:text-lg font-bold text-text-enterprise-primary mb-3">Experience</div>
              <div className="text-sm text-text-enterprise-tertiary font-semibold">
                {experienceAnalysis?.seniority_level_match ? 'Level ✓' : 'Level ✗'}
              </div>
            </div>

            {/* Education Match */}
            <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-xl p-6 lg:p-8 text-center">
              <div className={`text-5xl lg:text-6xl font-bold mb-3 ${getScoreColor(analysis.education_match_score)}`}>
                {analysis.education_match_score}%
              </div>
              <div className="text-base lg:text-lg font-bold text-text-enterprise-primary mb-3">Education</div>
              <div className="text-sm text-text-enterprise-tertiary font-semibold">
                {educationAnalysis?.education_level_match ? 'Level ✓' : 'Level ✗'}
              </div>
            </div>
          </div>

          {/* Detailed Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Skills Comparison */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-green-200 dark:border-gray-600 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-green-600" />
                Skills Analysis
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Match Score:</span>
                  <span className={`text-xl font-bold ${getScoreColor(analysis.skill_match_score)}`}>
                    {analysis.skill_match_score}%
                  </span>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Matched Mandatory Skills ({skillAnalysis?.mandatory_skills_match?.length || 0})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(skillAnalysis?.mandatory_skills_match || []).map((skill: string, index: number) => (
                        <span key={index} className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-sm rounded-lg font-medium border border-green-200 dark:border-green-700">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Missing Mandatory Skills ({skillAnalysis?.missing_mandatory_skills?.length || 0})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(skillAnalysis?.missing_mandatory_skills || []).map((skill: string, index: number) => (
                        <span key={index} className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-sm rounded-lg font-medium border border-red-200 dark:border-red-700">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Nice-to-Have Skills ({skillAnalysis?.nice_to_have_skills?.length || 0})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(skillAnalysis?.nice_to_have_skills || []).map((skill: string, index: number) => (
                        <span key={index} className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-sm rounded-lg font-medium border border-blue-200 dark:border-blue-700">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Experience & Other Factors */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-blue-200 dark:border-gray-600 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Experience & Fit
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-white dark:bg-gray-700 rounded-lg border border-blue-100 dark:border-gray-600">
                    <div className={`text-3xl font-bold mb-2 ${experienceAnalysis?.seniority_level_match ? 'text-green-500' : 'text-red-500'}`}>
                      {experienceAnalysis?.seniority_level_match ? '✓' : '✗'}
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Seniority Match</div>
                  </div>
                  <div className="text-center p-4 bg-white dark:bg-gray-700 rounded-lg border border-blue-100 dark:border-gray-600">
                    <div className={`text-3xl font-bold mb-2 ${experienceAnalysis?.domain_experience_match ? 'text-green-500' : 'text-red-500'}`}>
                      {experienceAnalysis?.domain_experience_match ? '✓' : '✗'}
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Domain Match</div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg border border-blue-100 dark:border-gray-600">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Experience Years:</span>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {experienceAnalysis?.candidate_experience_years || 0} / {experienceAnalysis?.required_experience_years || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg border border-blue-100 dark:border-gray-600">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Education Level:</span>
                    <span className={`text-sm font-semibold ${educationAnalysis?.education_level_match ? 'text-green-600' : 'text-red-600'}`}>
                      {educationAnalysis?.education_level_match ? '✓ Match' : '✗ No Match'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg border border-blue-100 dark:border-gray-600">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Leadership Experience:</span>
                    <span className={`text-sm font-semibold ${experienceAnalysis?.leadership_experience_match ? 'text-green-600' : 'text-red-600'}`}>
                      {experienceAnalysis?.leadership_experience_match ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Education Comparison */}
          {educationAnalysis && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-amber-200 dark:border-gray-600 shadow-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" />
                Education Comparison
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-amber-100 dark:border-gray-600">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Required Education</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white break-words">
                    {educationAnalysis.required_education || 'Not specified'}
                  </div>
                </div>
                <div className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-amber-100 dark:border-gray-600">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Candidate Education</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white break-words">
                    {educationAnalysis.candidate_education || 'Not provided'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-amber-100 dark:border-gray-600">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Level Comparison</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {educationAnalysis.level_comparison || '—'}
                  </div>
                </div>
                <div className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-amber-100 dark:border-gray-600">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Field of Study Match</div>
                  <div className="text-sm font-semibold">
                    <span className={`px-2 py-1 rounded-md ${
                      educationAnalysis.field_of_study_match === 'high' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      educationAnalysis.field_of_study_match === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {educationAnalysis.field_of_study_match || 'unknown'}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-amber-100 dark:border-gray-600">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Institution Tier Match</div>
                  <div className={`text-sm font-semibold ${educationAnalysis.institution_tier_match ? 'text-green-600' : 'text-red-600'}`}>
                    {educationAnalysis.institution_tier_match ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>

              {(educationAnalysis.similarities && educationAnalysis.similarities.length > 0) && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Similarities</div>
                  <div className="flex flex-wrap gap-2">
                    {educationAnalysis.similarities.map((s: string, i: number) => (
                      <span key={i} className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs rounded border border-green-200 dark:border-green-700">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(educationAnalysis.differences && educationAnalysis.differences.length > 0) && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Differences</div>
                  <div className="flex flex-wrap gap-2">
                    {educationAnalysis.differences.map((d: string, i: number) => (
                      <span key={i} className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs rounded border border-red-200 dark:border-red-700">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {educationAnalysis.score_reason && (
                <div className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-amber-100 dark:border-gray-600">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Reasoning</div>
                  <div className="text-sm text-gray-800 dark:text-gray-200">
                    {educationAnalysis.score_reason}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* JD Skills vs CV Matches */}
          <div className="bg-gradient-to-br from-glass-surface/40 to-glass-surface/20 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-glass-border/40 dark:border-gray-600 shadow-lg mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">JD Skills vs CV Matches</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm font-semibold text-text-enterprise-primary mb-2">Mandatory Skills</div>
                <div className="space-y-2">
                  {(jdMandatory as string[]).map((skill, idx) => {
                    const isMatch = matchedMandatory.includes(skill);
                    return (
                      <div key={idx} className={`flex items-center justify-between px-3 py-2 rounded-md border ${isMatch ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700'}`}>
                        <span className="text-sm text-gray-800 dark:text-gray-200">{skill}</span>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${isMatch ? 'bg-green-100 text-green-700 dark:bg-green-800/60 dark:text-green-200' : 'bg-red-100 text-red-700 dark:bg-red-800/60 dark:text-red-200'}`}>
                          {isMatch ? 'Matched' : 'Missing'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-text-enterprise-primary mb-2">Optional Skills</div>
                <div className="space-y-2">
                  {(jdOptional as string[]).map((skill, idx) => {
                    const isMatch = matchedOptional.includes(skill);
                    return (
                      <div key={idx} className={`flex items-center justify-between px-3 py-2 rounded-md border ${isMatch ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700' : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-700'}`}>
                        <span className="text-sm text-gray-800 dark:text-gray-200">{skill}</span>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${isMatch ? 'bg-green-100 text-green-700 dark:bg-green-800/60 dark:text-green-200' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-800/60 dark:text-yellow-200'}`}>
                          {isMatch ? 'Matched' : 'Not in CV'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Strengths and Concerns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Strengths */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-green-200 dark:border-gray-600 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <ThumbsUp className="w-5 h-5 text-green-600" />
                Key Strengths
              </h3>
              
              <div className="space-y-3">
                {strengths.map((strength: string, index: number) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-700 rounded-lg border border-green-100 dark:border-gray-600">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-1 flex-shrink-0"></div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">{strength}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Areas of Concern */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-amber-200 dark:border-gray-600 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Areas to Explore
              </h3>
              
              <div className="space-y-3">
                {concerns.map((concern: string, index: number) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-700 rounded-lg border border-amber-100 dark:border-gray-600">
                    <div className="w-3 h-3 bg-amber-500 rounded-full mt-1 flex-shrink-0"></div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">{concern}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>


          {/* Hygiene Questions for Call Engagement */}
          {hygieneQuestions.length > 0 && (
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-indigo-200 dark:border-gray-600 shadow-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-indigo-600" />
                Call Engagement Questions
                <span className="text-sm bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 px-2 py-1 rounded-full">
                  {hygieneQuestions.length} questions
                </span>
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Key questions to ask during the call to gather missing information and improve engagement
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {hygieneQuestions.map((question, index) => (
                  <div key={index} className={`p-4 rounded-lg border-l-4 ${
                    question.priority === 'high' 
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-400' 
                      : question.priority === 'medium'
                      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-400'
                      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-400'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        question.priority === 'high' 
                          ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200' 
                          : question.priority === 'medium'
                          ? 'bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200'
                          : 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                      }`}>
                        {question.priority.toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {question.category.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      {question.question}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {question.reason}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recruiter Insights */}
          {recruiterInsights && (
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-emerald-200 dark:border-gray-600 shadow-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-emerald-600" />
                Recruiter Insights
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Interview Focus Areas */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Interview Focus Areas</h4>
                  <ul className="space-y-2">
                    {recruiterInsights.interview_focus_areas?.map((area, index) => (
                      <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Technical Questions */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Technical Screening Questions</h4>
                  <ul className="space-y-2">
                    {recruiterInsights.technical_screening_questions?.map((question, index) => (
                      <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                        {question}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Behavioral Questions */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Behavioral Questions</h4>
                  <ul className="space-y-2">
                    {recruiterInsights.behavioral_questions?.map((question, index) => (
                      <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                        {question}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Red Flags */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Red Flags to Watch</h4>
                  <ul className="space-y-2">
                    {recruiterInsights.red_flags_to_watch?.map((flag, index) => (
                      <li key={index} className="text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                        {flag}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Analysis Button */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-700 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">Enhanced Analysis View</h3>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Experience the new compact, user-friendly analysis interface with call script integration
                </p>
              </div>
              <Button 
                onClick={() => navigate(`/candidates/${candidateId}/original-analysis/${requirementId}`)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-md font-medium"
              >
                <Zap className="w-4 h-4 mr-2" />
                View Enhanced Analysis
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Decision Actions</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg shadow-md font-medium">
                <CheckCircle className="w-4 h-4 mr-2" />
                Schedule Interview
              </Button>
              
              <Button variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20 px-6 py-3 rounded-lg font-medium">
                <FileText className="w-4 h-4 mr-2" />
                Request More Info
              </Button>
              
              <Button variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50 dark:border-purple-600 dark:text-purple-400 dark:hover:bg-purple-900/20 px-6 py-3 rounded-lg font-medium">
                <Users className="w-4 h-4 mr-2" />
                Add to Shortlist
              </Button>
              
              <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20 px-6 py-3 rounded-lg font-medium">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Reject Candidate
              </Button>
            </div>
          </div>

        </div>
      </div>

      {/* Status Update Modal */}
      {candidateId && requirementId && (
        <StatusUpdateModal
          isOpen={statusUpdateModalOpen}
          onClose={() => setStatusUpdateModalOpen(false)}
          candidateId={candidateId}
          requirementId={requirementId}
          currentStatus={currentSubmissionStatus}
          initialStatus={currentSubmissionStatus}
          onStatusUpdated={(updatedStatus) => {
            setCurrentSubmissionStatus(updatedStatus);
            // Refresh status
            const fetchStatus = async () => {
              try {
                const candidate = await apiClient.getCandidate(candidateId);
                if (candidate && candidate.submission_status) {
                  setCurrentSubmissionStatus(candidate.submission_status);
                }
              } catch (error) {
                console.error('Error refreshing status:', error);
              }
            };
            fetchStatus();
          }}
        />
      )}
    </ResponsiveContainer>
  );
};

export default CandidateAnalysisPage;
